import React from 'react';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { formatClock12 } from '../../utils/timesheet';

const LABELS = {
  later: 'Push the later entries back',
  earlier: 'Pull the earlier entries forward',
};

const range = (startTime, endTime) => `${formatClock12(startTime)} – ${formatClock12(endTime)}`;

/**
 * Offered when an entry is boxed in on both sides and overlapping entries are
 * turned off: each option (from planOverlapFix) says where this entry lands
 * and which neighbours move to make room. Nobody's length changes.
 */
export default function OverlapFixModal({ plan: planProp, taskById, onChoose, onApply, onClose, loading = false }) {
  const open = Boolean(planProp);
  const plan = useLastPresent(planProp); // keeps the dialog steady while it fades out
  const nameOf = (entry) => taskById.get(entry.taskId)?.name || 'Entry';

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Make room for this entry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {plan ? (
          <>
            <p className="ts-fix__intro">
              <strong>{nameOf(plan.entry)}</strong> would overlap the entries around it. Pick how to move them - every
              entry keeps its length.
            </p>
            <div className="ts-fix__options" role="radiogroup" aria-label="Ways to fix the overlap">
              {plan.options.map((option) => {
                const on = option.key === plan.choice;
                return (
                  <button
                    key={option.key}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`ts-fix__option${on ? ' is-on' : ''}`}
                    onClick={() => onChoose(option.key)}
                  >
                    <span className="ts-fix__head">
                      <i className={`bi ${on ? 'bi-record-circle-fill' : 'bi-circle'}`} aria-hidden="true" />
                      {LABELS[option.key]}
                    </span>
                    <span className="ts-fix__moves">
                      <span className="ts-fix__move is-self">
                        <span className="ts-fix__name">{nameOf(plan.entry)}</span>
                        <span className="ts-fix__times">{range(option.startTime, option.endTime)}</span>
                      </span>
                      {[...option.moves].sort((a, b) => a.startTime.localeCompare(b.startTime)).map((m) => (
                        <span key={m.entry.id} className="ts-fix__move">
                          <span className="ts-fix__name">{nameOf(m.entry)}</span>
                          <span className="ts-fix__times">
                            <s>{range(m.entry.startTime, m.entry.endTime)}</s>
                            <i className="bi bi-arrow-right" aria-hidden="true" />
                            {range(m.startTime, m.endTime)}
                          </span>
                        </span>
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="primary" onClick={onApply} loading={loading}>Move entries</Button>
      </Modal.Footer>
    </Modal>
  );
}
